import { useStore } from '@nanostores/react';
import { useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { clientFetch } from '~/lib/.client/fetch';
import { type SupportedAgents, getAgentName, getDefaultAgent } from '~/lib/agents';
import { getAgentForChat } from '~/lib/ai';
import { chatHooks } from '~/lib/chat-hooks';
import type { ChatHistoryItem } from '~/lib/persistence/chats';
import { chatStore } from '~/lib/stores/chat';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import type { Message } from '~/types/chat';

function updateAgent(chatList: ChatHistoryItem[], projectId?: string, agent?: SupportedAgents) {
  if (!projectId) {
    return;
  }

  chatStore.chatList.set(
    chatList.map((chat) => {
      return chat.projectId === projectId ? { ...chat, agent } : chat;
    }),
  );
}

export function useChatAgent() {
  const chatStarted = useStore(chatStore.started);
  const chatList = useStore(chatStore.chatList);
  const projectId = useStore(projectStore.projectId);
  const agentForNotStartedChat = useStore(chatStore.agentForNotStartedChat);

  const agent = useMemo(() => {
    if (!chatStarted) {
      return agentForNotStartedChat;
    }

    const defaultAgent = getDefaultAgent(userStore.getUser());

    if (!projectId) {
      return defaultAgent;
    }

    const chat = chatList.find((chat) => chat.projectId === projectId);

    if (!chat) {
      return defaultAgent;
    }

    return getAgentForChat(chat, userStore.getUser());
  }, [chatList, projectId, chatStarted, agentForNotStartedChat]);

  const setAgent = useCallback(
    async (agent: SupportedAgents) => {
      const currentAgent = chatList.find((chat) => chat.projectId === projectId)?.agent;

      const response = await clientFetch('/api/chat/agent', {
        method: 'POST',
        body: JSON.stringify({
          projectId,
          agent,
        }),
      });

      updateAgent(chatList, projectId, agent);

      if (!response.ok) {
        updateAgent(chatList, projectId, currentAgent);

        toast.error('Failed to update agent');

        return;
      } else {
        toast.success(`Switched agent to ${getAgentName(agent)}`);
      }

      const json = await response.json<{ messages: Message[] }>();

      chatHooks.setMessages(json.messages);
    },
    [chatList, projectId],
  );

  return {
    agent,
    setAgent,
  };
}
